/**
 * Admin — pending user approvals with flat/role/document context and
 * approve/reject actions (optimistic, reverting on error).
 * Wired: GET /api/admin/approvals, POST /api/admin/approvals/<userId>.
 * Port of screens-admin.jsx AdminApprovals.
 */
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { fmtWhen, str, useGateList } from '@/components/gate/api';
import { ModerationScreen, SmallButton } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Avatar, Button, Card, Hairline, Sheet } from '@/ui';

type Pending = { id: string; name: string; flat: string; role: string; doc: string; when: string; email: string; phone: string; city: string };

function flatCodeOf(u: unknown): string {
  const f = (u as { flat?: { code?: string } })?.flat;
  return (f && typeof f.code === 'string' ? f.code : str(u, 'flatCode')) ?? '';
}

function toRow(u: unknown, i: number): Pending {
  return {
    // Approvals are membership requests now: the row id IS the membership id
    // (POST /api/admin/approvals/<membershipId>). Display fields come from the
    // joined user (userName) + the membership (flat, residentType).
    id: str(u, 'membershipId', 'id', 'userId') ?? `u${i}`,
    name: str(u, 'userName', 'name', 'fullName') ?? 'Resident',
    flat: flatCodeOf(u),
    role: str(u, 'residentType', 'role') ?? '',
    doc: str(u, 'doc', 'document', 'documentType') ?? '',
    when: fmtWhen(str(u, 'createdAt', 'requestedAt'), str(u, 'when')),
    email: str(u, 'email') ?? '',
    phone: str(u, 'phone') ?? '',
    city: str(u, 'city') ?? '',
  };
}

/** One labelled line inside the confirm sheet. */
function DetailRow({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: space.s3, paddingVertical: 9 }}>
      <AppText variant="bodySm" tone="tertiary">{label}</AppText>
      <AppText variant="bodySm" weight="500" style={{ flexShrink: 1, textAlign: 'right' }}>{value}</AppText>
    </View>
  );
}

export default function AdminApprovalsScreen() {
  const { colors } = useTheme();
  const { items, state } = useGateList<unknown>('/api/admin/approvals', 'approvals', 'pending', 'users');
  const rows = items.map(toRow);
  const [decided, setDecided] = React.useState<Record<string, 'approved' | 'rejected'>>({});
  const [busy, setBusy] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  // Approve/Reject open a details sheet first — one accidental tap must never
  // admit someone into the society (user feedback 2026-08-22).
  const [confirm, setConfirm] = React.useState<{ row: Pending; action: 'approve' | 'reject' } | null>(null);
  // Decided rows drop out of the "pending" queue immediately instead of
  // lingering with a static Approved/Rejected label (audit 05 P2-1).
  const visibleRows = rows.filter((u) => !decided[u.id]);

  const act = (row: Pending, action: 'approve' | 'reject') => {
    const verdict = action === 'approve' ? 'approved' : 'rejected';
    setDecided((s) => ({ ...s, [row.id]: verdict })); // optimistic
    setBusy(row.id);
    setNotice(null);
    api.post<unknown>(`/api/admin/approvals/${row.id}`, { action })
      .catch(() => {
        setDecided((s) => {
          const next = { ...s };
          delete next[row.id];
          return next;
        }); // revert
        setNotice(`Could not ${action} ${row.name}. Try again.`);
      })
      .finally(() => setBusy(null));
  };

  return (
    <ModerationScreen
      title="Pending approvals"
      state={state}
      count={visibleRows.length}
      emptyIcon="user-plus"
      emptyTitle="No pending requests"
      emptyBody="New residents who sign up for your society will appear here for approval."
      notice={notice}
    >
      {visibleRows.map((u, i) => {
        const isNext = i === 0;
        return (
          <Card
            key={u.id}
            style={isNext ? { borderColor: colors.primary500, backgroundColor: colors.primary50 } : undefined}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
              <Avatar name={u.name} size={48} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <AppText variant="headingSm" weight="500">{u.name}</AppText>
                <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                  {[u.flat, u.role, u.when].filter(Boolean).join(' · ')}
                </AppText>
              </View>
            </View>
            {u.doc ? (
              <View style={{
                flexDirection: 'row', alignItems: 'center', gap: space.s2, marginTop: space.s3,
                backgroundColor: colors.canvas1, borderRadius: 10, paddingHorizontal: space.s3, paddingVertical: 10,
              }}>
                <Icon name="file-text" size={15} color={colors.textTertiary} />
                <AppText variant="bodySm" tone="secondary" style={{ flex: 1 }}>{u.doc} submitted</AppText>
              </View>
            ) : null}
            <View style={{ flexDirection: 'row', gap: space.s2, marginTop: space.s3 }}>
              <View style={{ flex: 1 }}>
                <SmallButton label="Reject" danger disabled={busy === u.id} onPress={() => setConfirm({ row: u, action: 'reject' })} />
              </View>
              <Button
                title="Approve"
                style={{ flex: 1 }}
                disabled={busy === u.id}
                onPress={() => setConfirm({ row: u, action: 'approve' })}
              />
            </View>
          </Card>
        );
      })}

      <Sheet visible={confirm !== null} onClose={() => setConfirm(null)}>
        {confirm ? (
          <View style={{ paddingBottom: space.s2 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, marginBottom: space.s3 }}>
              <Avatar name={confirm.row.name} size={44} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <AppText variant="headingSm" weight="600">{confirm.row.name}</AppText>
                <AppText variant="bodySm" tone="tertiary">
                  {confirm.action === 'approve' ? 'Approve this join request?' : 'Reject this join request?'}
                </AppText>
              </View>
            </View>
            <Hairline />
            <DetailRow label="Flat" value={confirm.row.flat} />
            <DetailRow label="Resident type" value={confirm.row.role} />
            <DetailRow label="Email" value={confirm.row.email} />
            <DetailRow label="Phone" value={confirm.row.phone || 'Not provided'} />
            <DetailRow label="Requested" value={confirm.row.when} />
            <View style={{ flexDirection: 'row', gap: space.s2, marginTop: space.s4 }}>
              <View style={{ flex: 1 }}>
                <SmallButton label="Cancel" onPress={() => setConfirm(null)} />
              </View>
              <Button
                title={confirm.action === 'approve' ? `Approve ${confirm.row.name.split(' ')[0]}` : `Reject ${confirm.row.name.split(' ')[0]}`}
                kind={confirm.action === 'reject' ? 'destructive' : undefined}
                style={{ flex: 2 }}
                onPress={() => { const c = confirm; setConfirm(null); act(c.row, c.action); }}
              />
            </View>
          </View>
        ) : null}
      </Sheet>
    </ModerationScreen>
  );
}
